/**
 * ChecklistItemCommentThread
 *
 * Thread de comentários de um item de checklist com visual idêntico ao da
 * timeline de andamentos (`TaskCommentReplySection`): múltiplos comentários,
 * @menções, anexos, edição inline e input com Ctrl+Enter / drag-and-drop.
 *
 * Reaproveita:
 *  - Hook `useTaskChecklist` (mutations upsertItemComment/deleteItemComment)
 *  - `ActivityCommentInput` (input premium com anexos + menções)
 *  - Utilitários de menção `formatMentionsForDisplay`, `stripMentionIds`,
 *    `extractMentionMap` e `useTenantMembers`.
 *  - `AttachmentThumbnail`, `ImageLightbox`, `DownloadAllZipButton`.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-07 v1.0.0 - Criação inicial (Fase 3 do Checklist)
 */
import { useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQueryClient } from '@tanstack/react-query';
import {
  Pencil, Trash2, X, Check, Loader2, Upload, CheckCircle2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
// Resolve o tipo de mídia do preview interno (imagem/vídeo/documento)
import { resolvePreviewMediaType } from '@/lib/attachment-preview';
import { DownloadAllZipButton } from '@/hooks/useDownloadAllAsZip';
import { ActivityCommentInput } from '@/components/crm/activities/ActivityCommentInput';
import { UserMentionPopover } from '@/components/crm/activities/UserMentionPopover';
import {
  formatMentionsForDisplay, stripMentionIds, extractMentionMap, useTenantMembers,
} from '@/hooks/useActivityComments';
import { uploadTaskAttachmentDirect, validateFile } from '@/hooks/useTaskAttachments';
import { useAuth } from '@/hooks/useAuth';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useToast } from '@/hooks/use-toast';
import { getMimeMediaType } from '@/lib/file-utils';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';
import type { ChecklistItemComment } from '@/hooks/useTaskChecklist';

const logger = createLogger('ChecklistItemCommentThread');

interface ChecklistItemCommentThreadProps {
  /** ID do item de checklist */
  itemId: string;
  /** ID da tarefa (para upload de anexos) */
  taskId: string;
  /** ID do tenant */
  tenantId: string;
  /** Lista de comentários já carregados pela query do checklist */
  comments: ChecklistItemComment[];
  /** Se true, esconde input e ações de editar/excluir (tarefa fechada) */
  readOnly?: boolean;
  /** Controle externo de expansão (gatilho fica na linha do item) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Cria/edita um comentário via RPC upsert */
  onUpsert: (params: {
    itemId: string;
    commentId?: string | null;
    content: string;
    mentionedUserIds: string[];
    attachmentIds?: string[];
    removedAttachmentIds?: string[];
  }) => Promise<string>;
  /** Exclui um comentário pelo ID */
  onDelete: (commentId: string) => Promise<unknown>;
}


interface MentionableUser {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

/**
 * Thread de comentários — cabeçalho colapsável + lista + input premium
 */
export function ChecklistItemCommentThread({
  itemId,
  taskId,
  tenantId,
  comments,
  readOnly,
  open,
  onOpenChange,
  onUpsert,
  onDelete,
}: ChecklistItemCommentThreadProps) {
  const { user } = useAuth();
  const { isAdmin } = usePermissionsContext();
  const { data: tenantMembers = [] } = useTenantMembers();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ------------- Estado de expansão + lightbox -------------
  // Suporta modo controlado (via prop) ou não-controlado (fallback interno)
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);


  // ------------- Estado de edição inline -------------
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [editMentionMap, setEditMentionMap] = useState<Record<string, string>>({});
  const [editMentionedUsers, setEditMentionedUsers] = useState<MentionableUser[]>([]);
  const [editRemovedAttachmentIds, setEditRemovedAttachmentIds] = useState<string[]>([]);
  const [editStagedFiles, setEditStagedFiles] = useState<File[]>([]);
  const [isUploadingEditFiles, setIsUploadingEditFiles] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editShowMentionPopover, setEditShowMentionPopover] = useState(false);
  const [editMentionSearch, setEditMentionSearch] = useState('');
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  // Refs espelho (evita stale closure em handlers de menção)
  const editingContentRef = useRef('');
  const editCursorPositionRef = useRef(0);
  const editMentionedUsersRef = useRef<MentionableUser[]>([]);

  // ------------- Estado de submissão do input principal -------------
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);

  const count = comments.length;

  // Reune todos os anexos para o lightbox
  const allMediaItems = useMemo<MediaItem[]>(() => {
    const items: MediaItem[] = [];
    comments.forEach((c) => {
      (c.attachments || []).forEach((a) => {
        items.push({
          url: a.public_url,
          type: resolvePreviewMediaType(a.file_type, (a as any).mime_type) ?? 'document',
          title: a.file_name,
        });
      });
    });
    return items;
  }, [comments]);

  const openLightbox = (url: string) => {
    const idx = allMediaItems.findIndex((i) => i.url === url);
    if (idx >= 0) {
      setLightboxIndex(idx);
      setLightboxOpen(true);
    }
  };

  // ================= Handlers =================

  /** Submete um novo comentário (via ActivityCommentInput) */
  const handleSubmitNew = async (content: string, mentionedUserIds: string[], files?: File[]) => {
    setIsSubmittingNew(true);
    try {
      // 1) Cria o comentário sem anexos
      const commentId = await onUpsert({
        itemId,
        commentId: null,
        content,
        mentionedUserIds,
      });

      // 2) Faz upload de cada arquivo já vinculado ao comentário
      if (files && files.length > 0 && commentId) {
        for (const f of files) {
          try {
            await uploadTaskAttachmentDirect(taskId, tenantId, f, undefined, undefined, commentId);
          } catch (err) {
            logger.warn('Falha ao subir anexo do comentário', { error: (err as Error).message });
          }
        }
        // Atualiza checklist e lista de anexos agregada
        queryClient.invalidateQueries({ queryKey: ['task-checklist', taskId] });
        queryClient.invalidateQueries({ queryKey: ['task-activity-attachments', taskId] });
      }
    } finally {
      setIsSubmittingNew(false);
    }
  };

  /** Entra em modo edição */
  const beginEdit = (c: ChecklistItemComment) => {
    const stripped = stripMentionIds(c.content);
    setEditingId(c.id);
    setEditingContent(stripped);
    editingContentRef.current = stripped;
    editCursorPositionRef.current = stripped.length;
    const map = extractMentionMap(c.content);
    setEditMentionMap(map);
    const users: MentionableUser[] = Object.entries(map).map(([name, id]) => ({
      id, full_name: name, avatar_url: null,
    }));
    setEditMentionedUsers(users);
    editMentionedUsersRef.current = users;
    setEditRemovedAttachmentIds([]);
    setEditStagedFiles([]);
    setEditShowMentionPopover(false);
    setEditMentionSearch('');
  };

  /** Cancela edição limpando estado */
  const cancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
    setEditMentionMap({});
    setEditMentionedUsers([]);
    setEditRemovedAttachmentIds([]);
    setEditStagedFiles([]);
    setEditShowMentionPopover(false);
    setEditMentionSearch('');
    editingContentRef.current = '';
    editCursorPositionRef.current = 0;
    editMentionedUsersRef.current = [];
  };

  /** Detecta '@' e abre popover de menções (edição) */
  const handleEditInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart || 0;
    setEditingContent(value);
    editingContentRef.current = value;
    editCursorPositionRef.current = cursor;

    const before = value.substring(0, cursor);
    const lastAt = before.lastIndexOf('@');
    if (lastAt !== -1) {
      const after = before.substring(lastAt + 1);
      const typingNew = !after.includes(' ') && !after.includes('\n');
      const already = editMentionedUsersRef.current.some((u) => after.startsWith(u.full_name));
      if (typingNew && !already) {
        setEditMentionSearch(after);
        setEditShowMentionPopover(true);
        return;
      }
    }
    setEditShowMentionPopover(false);
  };

  /** Insere menção escolhida no textarea de edição */
  const handleEditSelectMention = (u: MentionableUser) => {
    const cur = editingContentRef.current;
    const pos = editCursorPositionRef.current;
    const before = cur.substring(0, pos);
    const after = cur.substring(pos);
    const lastAt = before.lastIndexOf('@');
    if (lastAt === -1) return;

    const mentionText = `@${u.full_name} `;
    const next = cur.substring(0, lastAt) + mentionText + after;
    setEditingContent(next);
    editingContentRef.current = next;

    const users = [...editMentionedUsersRef.current.filter((x) => x.id !== u.id), u];
    setEditMentionedUsers(users);
    editMentionedUsersRef.current = users;
    setEditMentionMap((prev) => ({ ...prev, [u.full_name]: u.id }));

    const newPos = lastAt + mentionText.length;
    editCursorPositionRef.current = newPos;
    setTimeout(() => {
      editTextareaRef.current?.focus();
      editTextareaRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
    setEditShowMentionPopover(false);
  };

  /** Seleciona arquivos para anexar na edição */
  const handleEditFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid: File[] = [];
    for (const f of files) {
      const v = validateFile(f);
      if (!v.valid) {
        toast({ title: 'Arquivo inválido', description: v.error, variant: 'destructive' });
      } else {
        valid.push(f);
      }
    }
    setEditStagedFiles((prev) => [...prev, ...valid]);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  /** Confirma a edição — RPC + upload + remoções */
  const handleSaveEdit = async (commentId: string) => {
    const trimmed = editingContent.trim();
    if (!trimmed) return;

    // Reconstrói @Nome → @[Nome](uuid)
    let stored = trimmed;
    for (const [name, uuid] of Object.entries(editMentionMap)) {
      stored = stored.split(`@${name}`).join(`@[${name}](${uuid})`);
    }
    const finalIds = Object.entries(editMentionMap)
      .filter(([name]) => trimmed.includes(`@${name}`))
      .map(([, id]) => id);

    setIsSavingEdit(true);
    try {
      // 1) Atualiza texto/menções e remove anexos marcados via RPC
      await onUpsert({
        itemId,
        commentId,
        content: stored,
        mentionedUserIds: finalIds,
        removedAttachmentIds: editRemovedAttachmentIds,
      });

      // 2) Remove arquivos do storage para os anexos apagados
      for (const attId of editRemovedAttachmentIds) {
        try {
          // já foi removido do banco pela RPC; buscamos o path via fallback silencioso
          const c = comments.find((x) => x.id === commentId);
          const target = c?.attachments.find((a) => a.id === attId);
          if (target?.storage_path) {
            await supabase.storage.from('tarefas').remove([target.storage_path]);
          }
        } catch (err) {
          logger.warn('Falha ao remover arquivo do storage', { error: (err as Error).message });
        }
      }

      // 3) Upload de novos anexos vinculados ao comentário
      if (editStagedFiles.length > 0) {
        setIsUploadingEditFiles(true);
        for (const f of editStagedFiles) {
          try {
            await uploadTaskAttachmentDirect(taskId, tenantId, f, undefined, undefined, commentId);
          } catch (err) {
            logger.warn('Falha ao subir novo anexo na edição', { error: (err as Error).message });
          }
        }
        setIsUploadingEditFiles(false);
      }

      // 4) Invalida query do checklist e anexos agregados
      queryClient.invalidateQueries({ queryKey: ['task-checklist', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-activity-attachments', taskId] });

      cancelEdit();
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ================= Render =================

  return (
    <TooltipProvider>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Sem gatilho visual: a expansão é controlada pelo ícone de balão
            posicionado na linha do item (ChecklistItemRow). */}
        <CollapsibleContent className="space-y-3">
          {/* Lista de comentários */}
          {count > 0 && (
            <div className="space-y-2 pl-2 border-l-2 border-muted ml-2">
              {comments.map((c) => {
                const canManage = !readOnly && (c.author_id === user?.id || isAdmin);
                const editedFlag =
                  new Date(c.updated_at).getTime() > new Date(c.created_at).getTime() + 1000;



                return (
                  <div
                    key={c.id}
                    className="group flex gap-2 pl-2 py-1 rounded-r-md hover:bg-muted/30 transition-colors"
                  >
                    <SmartAvatar
                      src={c.author_avatar}
                      name={c.author_name ?? undefined}
                      size="xs"
                      className="flex-shrink-0 mt-0.5"
                    />

                    <div className="flex-1 min-w-0">
                      {/* Header do comentário */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">
                          {c.author_name ?? 'Usuário'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {format(new Date(c.created_at), 'dd/MM HH:mm', { locale: ptBR })}
                          {editedFlag && <span className="ml-1 italic">(editado)</span>}
                        </span>
                      </div>

                      {/* Conteúdo OU modo edição */}
                      {editingId === c.id ? (
                        <div className="mt-1 space-y-1.5">
                          <Textarea
                            ref={editTextareaRef}
                            value={editingContent}
                            onChange={handleEditInputChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') setEditShowMentionPopover(false);
                            }}
                            className="min-h-[50px] text-xs"
                            autoFocus
                          />

                          <UserMentionPopover
                            isOpen={editShowMentionPopover}
                            onClose={() => setEditShowMentionPopover(false)}
                            onSelect={handleEditSelectMention}
                            users={tenantMembers}
                            searchTerm={editMentionSearch}
                            anchorRef={editTextareaRef}
                          />

                          {/* Anexos existentes editáveis */}
                          {(() => {
                            const existing = (c.attachments || []).filter(
                              (a) => !editRemovedAttachmentIds.includes(a.id)
                            );
                            return existing.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {existing.map((a) => (
                                  <AttachmentThumbnail
                                    key={a.id}
                                    attachment={a as never}
                                    readOnly={false}
                                    className="w-12 h-12"
                                    onDelete={() =>
                                      setEditRemovedAttachmentIds((prev) => [...prev, a.id])
                                    }
                                  />
                                ))}
                              </div>
                            ) : null;
                          })()}

                          {/* Arquivos staged (novos) */}
                          {editStagedFiles.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {editStagedFiles.map((f, i) => (
                                <div key={`staged-${i}`} className="relative group">
                                  <div className="w-12 h-12 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                                    {f.type.startsWith('image/') ? (
                                      <img
                                        src={URL.createObjectURL(f)}
                                        alt={f.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <span className="text-[8px] text-muted-foreground text-center px-0.5 truncate">
                                        {f.name.split('.').pop()?.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditStagedFiles((prev) => prev.filter((_, j) => j !== i))
                                    }
                                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-white flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Anexar arquivo na edição */}
                          <button
                            type="button"
                            onClick={() => editFileInputRef.current?.click()}
                            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Upload className="h-3 w-3" />
                            Anexar arquivo
                          </button>
                          <input
                            ref={editFileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={handleEditFileSelect}
                          />

                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-[10px] px-2"
                              onClick={cancelEdit}
                              disabled={isSavingEdit || isUploadingEditFiles}
                            >
                              <X className="h-3 w-3 mr-0.5" />
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              className="h-6 text-[10px] px-2"
                              onClick={() => handleSaveEdit(c.id)}
                              disabled={isSavingEdit || isUploadingEditFiles || !editingContent.trim()}
                            >
                              {(isSavingEdit || isUploadingEditFiles) ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-0.5" />
                              ) : (
                                <Check className="h-3 w-3 mr-0.5" />
                              )}
                              Salvar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p
                          className="text-xs text-foreground mt-0.5 break-words"
                          dangerouslySetInnerHTML={{ __html: formatMentionsForDisplay(c.content) }}
                        />
                      )}

                      {/* Anexos (modo visualização) */}
                      {editingId !== c.id && c.attachments && c.attachments.length > 0 && (
                        <div className="mt-1.5">
                          <div className="flex justify-end mb-1">
                            <DownloadAllZipButton
                              attachments={c.attachments as never}
                              zipFileName={`anexos_comentario_${c.id.substring(0, 8)}.zip`}
                            />
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {c.attachments.map((a) => (
                              <AttachmentThumbnail
                                key={a.id}
                                attachment={a as never}
                                readOnly
                                className="w-12 h-12"
                                onClick={() => openLightbox(a.public_url)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ações (hover) */}
                    <div className="flex items-center gap-1">
                      {canManage && editingId !== c.id && (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                                onClick={() => beginEdit(c)}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">Editar</TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                                onClick={() => onDelete(c.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">Excluir comentário</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Input premium para novo comentário */}
          {!readOnly && (
            <div className="pl-2">
              <ActivityCommentInput
                onSubmit={handleSubmitNew}
                isSubmitting={isSubmittingNew}
                placeholder="Adicione um comentário... Use @ para mencionar"
                allowAttachments
              />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={allMediaItems}
        initialIndex={lightboxIndex}
      />

      {/* Referência não-usada (para satisfazer TS caso icons sejam removidos) */}
      <span className="hidden">
        <CheckCircle2 className="h-0 w-0" />
      </span>
    </TooltipProvider>
  );
}
